import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Wallet, ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Customer-dashboard wallet display — shows the current store-credit balance
 * and the last few transactions (credits from refunds, debits when applied to
 * a booking). Exposes a refresh hook for parent components to re-pull after
 * a refund or apply action.
 */
const WalletCard = React.forwardRef((props, ref) => {
  const [data, setData] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wallet/me`, { withCredentials: true });
      setData(data || { balance: 0, transactions: [] });
    } catch {
      // silent — empty wallet is fine
    }
    setLoading(false);
  }, []);
  React.useImperativeHandle(ref, () => ({ refresh }), [refresh]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return null;
  if (data.balance === 0 && (data.transactions || []).length === 0) return null;

  return (
    <Card className="bg-gradient-to-br from-brand-primary/10 to-brand-accent/5 border-brand-primary/30 mb-6"
      data-testid="customer-wallet-card">
      <CardHeader className="border-b border-brand-primary/20 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Wallet size={20} className="text-brand-primary" /> Store Credit
            </CardTitle>
            <CardDescription className="text-xs">
              Applied automatically at checkout — covers part or all of any booking.
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-bold text-brand-primary"
              data-testid="wallet-balance">
              ₹{Number(data.balance).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] tracking-wider uppercase text-text-secondary">Balance</div>
          </div>
        </div>
      </CardHeader>
      {(data.transactions || []).length > 0 && (
        <CardContent className="pt-3">
          <details>
            <summary className="text-xs text-text-secondary cursor-pointer hover:text-text-primary">
              View {data.transactions.length} recent transaction{data.transactions.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
              {data.transactions.map((t, i) => {
                const isCredit = t.type === 'credit';
                const Arrow = isCredit ? ArrowDown : ArrowUp;
                return (
                  <li key={i} className="flex items-center justify-between text-xs gap-2 py-1 border-b border-brand-primary/10 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Arrow size={12} weight="bold" className={isCredit ? 'text-green-600' : 'text-red-600'} />
                      <span className="truncate">{t.note || (isCredit ? 'Credit' : 'Debit')}</span>
                    </div>
                    <span className={`font-bold ${isCredit ? 'text-green-700' : 'text-red-700'}`}>
                      {isCredit ? '+' : '−'}₹{Number(t.amount).toLocaleString('en-IN')}
                    </span>
                  </li>
                );
              })}
            </ul>
          </details>
        </CardContent>
      )}
    </Card>
  );
});

WalletCard.displayName = 'WalletCard';
export default WalletCard;
